// Helpers for turning shared plain text into sanitizable note HTML.
// Notes are stored as rich-text HTML (RICH_TEXT_SANITIZE_CONFIG allows <a>).
// Used by the mobile share import paths (Android share-target.js, iOS
// native/index.js) and the note form. The browser extension mirrors the
// source-line logic separately (different bundle, same pattern as the
// duplicated sanitize/looksLikePrimarilyText helpers).

// http(s) URL run. Excludes whitespace and '<' so it never swallows a tag.
export const URL_RE = /(https?:\/\/[^\s<]+)/g

// Note title cap (matches backend PostCreateNoteRequest title max).
export const NOTE_TITLE_MAX = 100

// A URL text fragment (https://html.spec.whatwg.org/multipage/browsing-the-web.html#text-fragments)
// looks like `#:~:text=[prefix-,]start[,end][,-suffix]`. Browsers (Chrome's
// "Share highlight", Safari, etc.) append it when sharing a page selection, so
// its presence is a definitive "this is a highlight" marker.
export const TEXT_FRAGMENT_RE = /#:~:text=/i

export function hasTextFragment(url) {
  return !!url && TEXT_FRAGMENT_RE.test(url)
}

// Recover the highlighted text from a URL text fragment. Drops the optional
// `prefix-` / `-suffix` context tokens (literal '-' adjacent to the comma;
// real hyphens in the text are percent-encoded as %2D), decodes the rest, and
// joins a start,end range with an ellipsis. Returns '' when there is no fragment.
export function textFromFragment(url) {
  const m = /#:~:text=([^&]+)/i.exec(url || '')
  if (!m) return ''
  return m[1]
    .split(',')
    .filter(part => part && !part.endsWith('-') && !part.startsWith('-'))
    .map(part => {
      try {
        return decodeURIComponent(part)
      } catch {
        return part
      }
    })
    .join(' … ')
    .trim()
}

const ANCHOR_REL = 'noopener noreferrer'
const ANCHOR_TARGET = '_blank'

export function escapeHtml(text) {
  if (!text) return ''
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

// Wrap http(s) URLs in an HTML-escaped string with anchors. Input MUST already
// be HTML-escaped (so we never produce unsafe markup from the source text).
export function linkifyEscaped(escaped) {
  if (!escaped) return ''
  return escaped.replace(URL_RE, url =>
    `<a href="${url}" target="${ANCHOR_TARGET}" rel="${ANCHOR_REL}">${url}</a>`,
  )
}

// Bare clickable source line: link text = title when present, else the URL.
// Preceded by a blank line so the source is visually separated from the body.
export function buildSourceLineHtml(url, title) {
  if (!url) return ''
  const label = (title && title.trim()) || url
  return `<p><br></p><p><a href="${escapeHtml(url)}" target="${ANCHOR_TARGET}" rel="${ANCHOR_REL}">${escapeHtml(label)}</a></p>`
}

// Build note HTML from plain shared text: escape, linkify inline URLs, wrap each
// non-empty line in <p>. When sourceUrl is given, append a bare source line.
export function textToNoteHtml(text, { sourceUrl, sourceTitle } = {}) {
  let html = (text || '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => `<p>${linkifyEscaped(escapeHtml(line))}</p>`)
    .join('')

  if (sourceUrl) html += buildSourceLineHtml(sourceUrl, sourceTitle)
  return html
}
